import { Box } from '@mui/material'
import AppBar from '@mui/material/AppBar'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import React, { useMemo } from 'react'

import { SearchBar } from '#/components/AdvancedSearch'
import FormatCell from '#/components/FormatCell'
import { Icon } from '#/components/Icon'
import Notifications from '#/components/Notification'
import { ReleaseNote } from '#/components/ReleaseNotes'
import UserMenu from '#/components/UserMenu'
import { useFarm } from '#/store/ducks/farm'
import { usePermissions } from '#/store/ducks/permissions'
import useAppContext from '#/store/hooks/useAppContext'
import useWidth from '#/store/hooks/useWidth'
import { isIOS } from '#/utils/device'

const drawerWidth = 240

const Wrapper = styled(AppBar)(({ theme }) => ({
  zIndex: 5,
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}))

const Bar = styled(Toolbar)(({ theme }) => ({
  justifyContent: 'space-between',
  [theme.breakpoints.up('sm')]: {
    height: '64px',
  },
  [theme.breakpoints.up('xs')]: {
    paddingLeft: 0,
    minHeight: '48px',
  },
}))

const FarmWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: 10,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const MobileMenu = styled(IconButton)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    marginRight: theme.spacing(2),
  },
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}))

export default function Header({ setMobileOpen, setDesktopOpen }) {
  const width = useWidth()
  const context = useAppContext()
  const { slug: currentFarmSlug, code: currentFarmCode, name: currentFarmName } = useFarm()

  const handleDrawerToggle = () => {
    if (['md', 'sm', 'xs'].includes(width)) {
      setMobileOpen(prevState => !prevState)
    } else if (['lg', 'xl'].includes(width)) {
      setDesktopOpen(prevState => !prevState)
    }
  }
  const permissions = usePermissions()

  const FarmInfo = () => {
    if (currentFarmCode) {
      return (
        <FarmWrapper>
          {width === 'xs' ? currentFarmCode : [currentFarmCode, currentFarmName].join(' - ')}
        </FarmWrapper>
      )
    }
  }

  const FarmMenuRightItems = useMemo(() => {
    const items = []
    switch (context) {
      case 'farm':
        items.push(
          <React.Fragment key="farm-right-menu-itens">
            {/*FARM NAME*/}
            <FarmInfo />
            {/*FARM STATUS*/}
            <div id="farm-status">
              {<FormatCell type="farm_coverage" value={{ variant: 'icon' }} />}
            </div>
            {/* NOTIFICATIONS */}
            <Notifications context="farm" key="farm-jobs" />
            {/* RELEASE NOTES MODAL */}
            {/* Não mostra release notes em dispositivos iOS (iPhone, iPad, iPod) */}
            {!isIOS && <ReleaseNote />}
          </React.Fragment>,
        )
        break

      case 'company':
        if (permissions.company.list_company_job) {
          items.push(<Notifications context="company" key="company-jobs" />)
        }
        break

      default:
        if (permissions.system.list_system_job) {
          items.push(<Notifications context="system" key="system-jobs" />)
        }
        break
    }

    return items
  }, [permissions, context, width, currentFarmSlug])

  return (
    <Wrapper position="sticky">
      <Bar>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {/* MOBILE MENU */}
          <MobileMenu color="inherit" onClick={handleDrawerToggle} size="large">
            <Icon size="large" name="menu" />
          </MobileMenu>
          <SearchBar />
        </Stack>
        <Stack direction="row" spacing={1}>
          {FarmMenuRightItems}
          {/* USER MENU */}
          <UserMenu />
        </Stack>
      </Bar>
    </Wrapper>
  )
}
